import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  FormHelperText,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormLabel,
  InputAdornment
} from '@mui/material';
import { 
  Close as CloseIcon, 
  CalendarMonth as CalendarIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { EventType, EventFormData } from '../../types/Event';
import CoverPhotoUpload from './CoverPhotoUpload';

// Event type options
const eventTypes: { value: EventType; label: string }[] = [
  { value: 'social', label: 'Social Gathering' },
  { value: 'family', label: 'Family Event' },
  { value: 'cultural', label: 'Cultural Event' },
  { value: 'sports', label: 'Sports Event' },
  { value: 'business', label: 'Business Event' },
  { value: 'other', label: 'Other' }
];

// Recurrence patterns
const recurrencePatterns = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' }
];

interface AddEventDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (eventData: EventFormData) => Promise<void>;
}

const AddEventDialog: React.FC<AddEventDialogProps> = ({ open, onClose, onSubmit }) => {
  const [eventData, setEventData] = useState<Partial<EventFormData>>({
    title: '',
    eventType: 'social',
    location: null,
    startDate: new Date(),
    endDate: new Date(Date.now() + 2 * 60 * 60 * 1000), // Default to 2 hours later
    description: '',
    isPublic: false
  });
  
  const [locationInput, setLocationInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
  
  // Recurring event state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState('weekly');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceEndType, setRecurrenceEndType] = useState('occurrences');
  const [recurrenceEndOccurrences, setRecurrenceEndOccurrences] = useState(10);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(
    new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) // Default to 90 days from now
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name) {
      setEventData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name) {
      setEventData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSetLocation = () => {
    if (locationInput) {
      setEventData(prev => ({
        ...prev,
        location: {
          name: locationInput,
          address: locationInput,
          coordinates: [0, 0] // Placeholder coordinates
        }
      }));
    }
  };

  const handleDateChange = (date: Date | null, field: 'startDate' | 'endDate') => {
    setEventData(prev => ({ ...prev, [field]: date }));
  };
  
  const handleRecurrenceIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setRecurrenceInterval(value > 0 ? value : 1);
  };
  
  const handleRecurrenceEndOccurrencesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setRecurrenceEndOccurrences(value > 0 ? value : 1);
  };
  
  const handleCoverPhotoChange = (imageUrl: string | null, file: File | null) => {
    setCoverPhotoUrl(imageUrl);
    setCoverPhotoFile(file);
  };

  const handleSubmit = async () => {
    if (!eventData.title || !eventData.startDate || !locationInput) {
      setError('Please fill in all required fields (title, date, location)');
      return;
    }
    
    if (!eventData.location && locationInput) {
      handleSetLocation();
    }

    try {
      setLoading(true);
      setError(null);
      
      // Build recurrence data if enabled
      const recurrenceData = isRecurring ? {
        pattern: recurrencePattern,
        interval: recurrenceInterval,
        endType: recurrenceEndType,
        endOccurrences: recurrenceEndOccurrences,
        endDate: recurrenceEndDate
      } : null;
      
      // Add cover image and recurrence to event data
      const completeEventData = {
        ...eventData,
        coverImage: coverPhotoFile,
        recurrence: recurrenceData
      } as EventFormData;
      
      await onSubmit(completeEventData);
      handleReset();
      onClose();
    } catch (err) {
      console.error('Error creating event:', err);
      setError('Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEventData({
      title: '',
      eventType: 'social',
      location: null,
      startDate: new Date(),
      endDate: new Date(Date.now() + 2 * 60 * 60 * 1000),
      description: '',
      isPublic: false
    });
    setLocationInput('');
    setError(null);
    setCoverPhotoUrl(null);
    setCoverPhotoFile(null);
    setIsRecurring(false);
    setRecurrencePattern('weekly');
    setRecurrenceInterval(1);
    setRecurrenceEndType('occurrences');
    setRecurrenceEndOccurrences(10);
    setRecurrenceEndDate(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Create New Event</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fill in the details to create a new event. Add a cover photo and set up recurring options if needed.
          </Typography>
          
          {error && (
            <Box sx={{ mb: 2 }}>
              <Typography color="error" variant="body2">{error}</Typography>
            </Box>
          )}
          
          <Grid container spacing={2}>
            {/* Event Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                name="title"
                value={eventData.title}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            {/* Destination */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                required
              />
            </Grid>
            
            {/* Date Range */}
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Start Date & Time"
                value={eventData.startDate}
                onChange={(date) => handleDateChange(date as Date | null, 'startDate')}
              
                renderInput={params => (
                  <TextField {...params} fullWidth required />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="End Date & Time"
                value={eventData.endDate}
                onChange={(date) => handleDateChange(date as Date | null, 'endDate')}
                renderInput={params => (
                  <TextField {...params} fullWidth required />
                )}
              />
            </Grid>
            
            {/* Event Type */}
            <Grid item xs={12} sm={6}>
              
              <FormControl fullWidth>
                <InputLabel id="event-type-label">Event Type</InputLabel>
                <Select
                  labelId="event-type-label"
                  name="eventType"
                  value={eventData.eventType}
                  onChange={handleSelectChange}
                  label="Event Type"
                >
                  {eventTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Cover Photo */}
            <Grid item xs={12}>
              <CoverPhotoUpload
                initialImageUrl={coverPhotoUrl}
                onChange={handleCoverPhotoChange}
                entityId={`event_${Date.now()}`}
                folder="events/covers"
                eventTitle={eventData.title || 'Event'}
                eventDescription={eventData.description || ''}
                eventType={eventData.eventType || 'social'}
              />
            </Grid>
            
            {/* Recurring Event Toggle */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <RepeatIcon sx={{ mr: 1 }} />
                      <Typography>Recurring Event</Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>
            
            {/* Recurring Event Options */}
            {isRecurring && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Recurrence Pattern
                  </Typography>
                </Grid>
                
                {/* Recurrence Pattern */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Repeat</InputLabel>
                    <Select
                      value={recurrencePattern}
                      label="Repeat"
                      onChange={(e) => setRecurrencePattern(e.target.value)}
                    >
                      {recurrencePatterns.map(pattern => (
                        <MenuItem key={pattern.value} value={pattern.value}>
                          {pattern.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Recurrence Interval */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`Every ${recurrenceInterval} ${recurrencePattern}${recurrenceInterval > 1 ? 's' : ''}`}
                    type="number"
                    InputProps={{
                      inputProps: { min: 1 }
                    }}
                    value={recurrenceInterval}
                    onChange={handleRecurrenceIntervalChange}
                  />
                </Grid>
                
                {/* Recurrence End */}
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Ends</FormLabel>
                    <RadioGroup
                      row
                      value={recurrenceEndType}
                      onChange={(e) => setRecurrenceEndType(e.target.value)}
                    >
                      <FormControlLabel 
                        value="occurrences" 
                        control={<Radio />} 
                        label="After" 
                      />
                      <FormControlLabel 
                        value="date" 
                        control={<Radio />} 
                        label="On date" 
                      />
                      <FormControlLabel 
                        value="never" 
                        control={<Radio />} 
                        label="Never" 
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                {recurrenceEndType === 'occurrences' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Number of occurrences"
                      type="number"
                      InputProps={{
                        inputProps: { min: 1 },
                        endAdornment: <InputAdornment position="end">times</InputAdornment>
                      }}
                      value={recurrenceEndOccurrences}
                      onChange={handleRecurrenceEndOccurrencesChange}
                    />
                  </Grid>
                )}
                
                {recurrenceEndType === 'date' && (
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="End Date"
                      value={recurrenceEndDate}
                      onChange={(date) => setRecurrenceEndDate(date as Date | null)}
                     
                      renderInput={params => (
                        <TextField {...params} fullWidth required={recurrenceEndType === 'date'} />
                      )}
                    />
                  </Grid>
                )}
              </>
            )}
            
            {/* Description - Optional */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                name="description"
                value={eventData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddEventDialog; 